/**
 * The ambient ash loop — and the brand lockup over it — behind the auth screens (login, register, forgot
 * password, reset password, check email, verify email).
 *
 * Those screens are CORE markup (`core/auth/*`) and the game must not edit
 * them, so the backdrop is attached from the game side onto the one hook core
 * already gives us: the `.bg-auth-gradient` root each screen renders. The
 * still plate is painted by that element's own `::before` (see
 * styles/app.css), so the picture is there on the first frame whether or not
 * the video ever starts.
 *
 * Mounted from main.ts alongside the "‹ Back" control, the same way.
 */
import { LOOPS, bindVisibilityPause, createAmbientVideo, safePlay } from './game/ambient-loop';

/**
 * Attach the loop to whatever auth screen is currently mounted inside `root`.
 * Returns a teardown that removes the video and its listener.
 */
export function attachAuthBackdrop(root: ParentNode = document, doc: Document = document): () => void {
  const host = root.querySelector('.bg-auth-gradient');
  if (!(host instanceof HTMLElement)) return () => {};

  // No poster: `.bg-auth-gradient::before` already paints `backdrops/auth.webp`
  // as the still, and that is a DIFFERENT crop from the loop's poster frame —
  // setting both put two pictures on the pane until the first frame decoded.
  const video = createAmbientVideo(LOOPS.auth, doc, { poster: false });
  if (!video) return () => {};

  // The video goes inside a HOST DIV rather than straight onto the root, for
  // two reasons that both showed on the running build:
  //
  //  1. From 900px up `.bg-auth-gradient` is a CSS grid and `> *` carries
  //     `grid-column: 2`. An absolutely positioned grid child is laid out
  //     against its GRID AREA, not the padding box — so the bare <video>
  //     resolved `inset: -4% var(--auth-pane) …` inside the form pane, whose
  //     width IS `var(--auth-pane)`: the right offset ate the whole box and
  //     the loop collapsed to zero width. The host pins `grid-column: 1 / -1`.
  //  2. A replaced element cannot carry the seam-and-foot shading that
  //     `::before` paints over the still. The host's own `::after` does, so
  //     the pane joint reads the same whether the still or the loop is up.
  const shell = doc.createElement('div');
  shell.setAttribute('data-ambient-loop-host', '');
  shell.setAttribute('aria-hidden', 'true');
  shell.appendChild(video);

  // First child: the card and every other mounted node must paint over it, and
  // the ≥900px layout places children into the form pane by document order.
  host.insertBefore(shell, host.firstChild);
  const unbind = bindVisibilityPause(video, doc);
  safePlay(video);

  return () => {
    unbind();
    video.pause();
    video.removeAttribute('src');
    shell.remove();
  };
}

/** The lockup's natural size (public/landing/logo.webp) — written out so the
 *  element reserves its own box and the art pane never reflows around it. */
export const AUTH_LOGO_SRC = '/landing/logo.webp';
export const AUTH_LOGO_W = 825;
export const AUTH_LOGO_H = 256;

/**
 * THE EMBLEM OVER THE ART (owner, 21.09 — "put the logo over the animation").
 *
 * From 900px up the auth screens are two panes: the ambient art on the left,
 * the form on the right. The moon-and-wordmark lockup is centred on the ART
 * pane only — it is decoration over a picture, so it is `aria-hidden` and not
 * focusable; the card's own letterhead is what actually names the place.
 *
 * Below 900px the art pane does not exist (the art runs full-bleed behind the
 * card), so `styles/app.css` hides this outright rather than letting it land
 * on top of the form. That is why it is one absolutely positioned element with
 * no layout of its own: nothing reflows when it goes away.
 */
export function attachAuthLogo(root: ParentNode = document, doc: Document = document): () => void {
  const host = root.querySelector('.bg-auth-gradient');
  if (!(host instanceof HTMLElement)) return () => {};

  const img = doc.createElement('img');
  img.setAttribute('data-auth-logo', '');
  img.src = AUTH_LOGO_SRC;
  img.alt = '';
  img.width = AUTH_LOGO_W;
  img.height = AUTH_LOGO_H;
  img.setAttribute('aria-hidden', 'true');
  img.setAttribute('loading', 'lazy');
  img.setAttribute('decoding', 'async');
  // after the loop host, before the card: it paints over the art, under nothing.
  const loopHost = host.querySelector('[data-ambient-loop-host]');
  if (loopHost) loopHost.insertAdjacentElement('afterend', img);
  else host.insertBefore(img, host.firstChild);

  return () => img.remove();
}
